import type { CartPrice, CartPriceBookingInput } from 'types/cartPricing';

/** One date of a stay and what one unit of a tier cost on it. */
export interface DatedRate {
  /** Local "YYYY-MM-DD". */
  date: string;
  price: number;
}

/** A tier as the group page holds it: its identity, its name for the quote, and how many are picked. */
export interface StayQuoteTier {
  id: string;
  name: string;
  participants: number;
}

export interface StayQuoteTax {
  amount: number;
  id: string;
  name: string | null;
}

/** What a picked stay costs, per tier and in all, as the server prices it. */
export interface StayQuote {
  /** The picked tier's line for the whole stay at its picked quantity, by tier name. */
  lineSubtotalByTierName: Record<string, number>;
  /** What one of each tier cost on each date of the stay, by tier name, for a rate that moves by date. */
  ratesByTierName: Record<string, DatedRate[]>;
  /** What one of each tier costs for the whole stay, by tier name. */
  stayPriceByTierName: Record<string, number>;
  subtotal: number;
  taxes: StayQuoteTax[];
  total: number;
}

/**
 * One quote line per tier, at the picked quantity or one — so every tier has a stay price to show even
 * before anyone picks it, and the picked ones price the summary at the numbers picked.
 */
export const buildStayQuoteBookings = ({
  durationMinutes,
  experienceId,
  startDateTime,
  tiers,
}: {
  durationMinutes: number;
  experienceId: string;
  startDateTime: string;
  tiers: readonly StayQuoteTier[];
}): CartPriceBookingInput[] =>
  tiers.map((tier) => ({
    durationMinutes,
    experienceId,
    lineId: tier.id,
    priceTierName: tier.name,
    quantity: Math.max(1, tier.participants),
    startDateTime,
  }));

/**
 * The stay's figures read back off the quote.
 *
 * A tier's stay price is its line divided by the quantity quoted, so it reads the same whether one or
 * three are picked; the summary sums only the lines whose tier someone actually picked, so an unpicked
 * tier's placeholder line never reaches the total.
 */
export const summariseStayQuote = (
  cart: CartPrice | null,
  tiers: readonly StayQuoteTier[],
): StayQuote | null => {
  if (!cart) {
    return null;
  }
  const lines = tiers.flatMap((tier) => {
    const line = cart.bookings.find((booking) => booking.lineId === tier.id);
    return line ? [{ line, tier }] : [];
  });
  const picked = lines.filter(({ tier }) => tier.participants > 0);

  const taxes = Object.values(
    picked
      .flatMap(({ line }) => line.taxBreakdown)
      .reduce<Record<string, StayQuoteTax>>((byId, entry) => {
        const current = byId[entry.id];
        return {
          ...byId,
          [entry.id]: {
            amount: (current?.amount ?? 0) + entry.computedAmount,
            id: entry.id,
            name: current?.name ?? entry.name ?? null,
          },
        };
      }, {}),
  );

  return {
    lineSubtotalByTierName: Object.fromEntries(
      picked.map(({ line, tier }) => [tier.name, line.subtotal]),
    ),
    ratesByTierName: Object.fromEntries(
      lines.map(({ line, tier }) => [
        tier.name,
        line.pricedUnits
          .filter((unit) => unit.priceTierName === tier.name)
          .map((unit) => ({ date: unit.date, price: unit.pricePerUnit })),
      ]),
    ),
    stayPriceByTierName: Object.fromEntries(
      lines.map(({ line, tier }) => [
        tier.name,
        line.subtotal / Math.max(1, tier.participants),
      ]),
    ),
    subtotal: picked.reduce((sum, { line }) => sum + line.subtotal, 0),
    taxes,
    total: picked.reduce((sum, { line }) => sum + line.total, 0),
  };
};
